import React, { useEffect, useState } from 'react';
import { Input, Switch, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import ShopStatus from 'components/shop-status';
import LiveTag from 'components/live-tag';
import { ENUM_SHOP_STATUS } from 'utils/enum';
import SingleBottomNav from 'components/base/single-bottom-nav';
import { apiService } from 'services/base.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useEditShopContext from 'contexts/shop.context';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

type Props = {
  onEditComplete: () => void;
};

const EditShopDetails = ({ onEditComplete }: Props) => {
  const { shopDetails, setShopDetails, setDisabled } = useEditShopContext();
  const [address, setAddress] = useState(shopDetails.address.address_line);
  const [loading, setLoading] = useState(false);

  const { shop_id } = useParams();

  const EMAIL_REGEX = /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  const PHONE_REGEX = /^(?:\+234|0)[789][01]\d{8}$/;

  const getAddressDetails = async (value: string) => {
    setAddress(value);
    const results = await geocodeByAddress(value);

    if (results) {
      const latLng = await getLatLng(results[0]);
      if (latLng) {
        const addressComponents = results[0];

        setShopDetails({
          ...shopDetails,
          address: {
            address_line: value,
            latitude: latLng.lat,
            longitude: latLng.lng,
            state:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('administrative_area_level_1');
              })?.long_name ?? '',
            country:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('country');
              })?.long_name ?? '',
            country_code:
              addressComponents.address_components.find((addr) => {
                return addr.types.includes('country');
              })?.short_name ?? ''
          }
        });
      }
    }
  };

  const handleUpdateShop = async () => {
    setLoading(true);
    const payload = {
      name: shopDetails.name,
      tagline: shopDetails.short_text,
      description: shopDetails.description,
      phone: shopDetails.phone_numbers,
      email: shopDetails.email,
      address: shopDetails.address,
      shop_info: shopDetails.shopStatus.map((status) => ({
        name: status,
        active: true,
        note: status === 'coming_soon' ? shopDetails.brandStatustext : null
      })),
      status: shopDetails.live
    };
    try {
      await apiService.put(`shops/${shop_id}`, { data: payload });

      onEditComplete();
      toast(<ToastMessage type='success' message={`Shop updated successfully`} />);
    } catch {
      toast(<ToastMessage type='error' message={`There was a problem updating shop `} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const disabled = ![shopDetails?.name, shopDetails?.short_text, shopDetails?.description].every(
      Boolean
    );

    setDisabled(disabled);
  }, [shopDetails]);

  const live = shopDetails?.live === ENUM_SHOP_STATUS.active;
  const searchOptions = {
    componentRestrictions: { country: ['ng'] }
  };

  return (
    <div className='space-y-4'>
      <Input
        type='text'
        label='Shop Name'
        placeholder='Enter shop name'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={shopDetails?.name}
        onChange={(e) => setShopDetails({ ...shopDetails, name: e.target.value })}
      />
      <Input
        type='text'
        label='Create shop Tagline'
        placeholder='Enter shop tagline'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={shopDetails?.short_text}
        maxLength={15}
        onChange={(e) => setShopDetails({ ...shopDetails, short_text: e.target.value })}
      />

      <Textarea
        className='w-full mt-1'
        placeholder='Describe the shop'
        value={shopDetails?.description}
        onChange={(e) => setShopDetails({ ...shopDetails, description: e.target.value })}
        label='Shop Description'
      />

      <Input
        type='text'
        label='Phone Number'
        placeholder='Enter phone number'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={shopDetails?.phone}
        maxLength={14}
        onChange={(e) => {
          setShopDetails({ ...shopDetails, phone: e.target.value });
        }}
      />
      {shopDetails.phone && !PHONE_REGEX.test(shopDetails.phone) && (
        <p className='text-red-5'>Enter valid phone number</p>
      )}

      <Input
        type='text'
        label='Email Address'
        placeholder='Enter email address'
        className='w-full mt-4'
        inputClass='px-[20px] py-[14px]'
        value={shopDetails?.email}
        onChange={(e) => {
          setShopDetails({ ...shopDetails, email: e.target.value });
        }}
      />
      {shopDetails.email && !EMAIL_REGEX.test(shopDetails.email) && (
        <p className='text-red-5 '>Enter valid email address</p>
      )}

      <PlacesAutocomplete
        debounce={600}
        value={address}
        onChange={setAddress}
        onSelect={getAddressDetails}
        searchOptions={searchOptions}>
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <Input
              {...getInputProps({
                placeholder: 'Enter address...',
                className: 'mb-2'
              })}
              label='Shop Address'
            />
            <div className=' '>
              {loading && <div>Loading...</div>}
              {suggestions.map((suggestion, i) => {
                const className = 'px-3 py-2 ';
                const style = suggestion.active
                  ? {
                      backgroundColor: '#F5F5F5',
                      transition: 'all .3s',
                      cursor: 'pointer'
                    }
                  : { backgroundColor: '#ffffff', cursor: 'pointer' };

                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style
                    })}
                    key={i}>
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>

      <div className='my-8'>
        <ShopStatus
          comment={shopDetails.brandStatustext}
          setComment={(k) => setShopDetails({ ...shopDetails, brandStatustext: k })}
          className='my-6'
          selectedShopStatus={shopDetails.shopStatus}
          setSelectedShopStatus={(d) => setShopDetails({ ...shopDetails, shopStatus: d })}
        />
      </div>

      <div className='border py-[20px] px-[24px] border-[#EDEDEE] rounded-xl flex justify-between my-8'>
        <div className='flex items-center justify-start gap-7'>
          <p className='text-xs font-medium'>Brand Active</p>
          <LiveTag live={live} />
        </div>
        <Switch
          active={live}
          onChange={(e) =>
            setShopDetails({
              ...shopDetails,
              live: e ? ENUM_SHOP_STATUS.active : ENUM_SHOP_STATUS.inactive
            })
          }
          label='Roll Back'
        />
      </div>
      <SingleBottomNav
        loading={loading}
        onConfirm={handleUpdateShop}
        backBtndisabled
        btnDisabled={loading}
      />
    </div>
  );
};

export default EditShopDetails;
